import { NextRouter } from 'next/router';

import { RedirectConfigProps } from '../../../types/pages/generic/redirectConfig';
import { ServerSideProps } from '../../../types/pages/generic/serverSideProps';
import { User } from '../../../types/api/UsersTypes';
import {
  SubscriptionDetail,
  PlanVariantResponse,
  SubscriptionInterval
} from '../../../types/api/SubscriptionTypes';
import { PlanType } from '../../../types/components/pricing/PlanVariantsWrapper';
import { StoryContent, PlanCardDetails, PageContent } from '../../../types/api/StoryblockTypes';
import { PlanVariantDetailsCardComponentProps } from '../../../types/components/pricing/PlanVariantDetailsCard';
import { LicenseId } from '../../../types/api/LicenseTypesTypes';
import { PricingPageServersideProps } from '../../../types/components/pricing/PricingPage';

import storyblockHelpers from '../../../helpers/storyblockHelpers';
import errorRedirectHandler from '../../../helpers/errorRedirectHandler';
import subscriptionUpgradeHelpers from '../../../helpers/subscriptionUpgradeHelpers';
import { decodeLicenseId } from '../../../helpers/enterpriseLicenseHelpers';

export const fetchPricingServersideProps = async (): Promise<
  ServerSideProps<PricingPageServersideProps> | RedirectConfigProps
> => {
  const { getCachedContent } = storyblockHelpers;
  let pageContent: PageContent;
  try {
    pageContent = await getCachedContent({
      sort_by: 'first_published_at:desc',
      content_type: 'pricing-so'
    });
  } catch (error) {
    return errorRedirectHandler(error);
  }
  return {
    props: {
      pageContent: pageContent?.stories[0].content
    }
  };
};

const filterPlanCards = (
  user: User,
  subscription: SubscriptionDetail,
  config?: { excludeOption: PlanType | Array<PlanType> }
): Array<PlanType> => {
  const ret: Array<PlanType> = [];
  const { getMinimalLicenseIdForUpgrade } = subscriptionUpgradeHelpers;
  const minLicenseId = getMinimalLicenseIdForUpgrade(user, subscription);
  if (!user?.id || (!!user?.id && !subscription && !user?.free_subscriber)) ret.push('free');

  switch (minLicenseId as number) {
    case 3:
    case 4:
    case 5:
    case 6:
      ret.push('enterprise');
      break;
    case 2:
      ret.push('startup', 'enterprise');
      break;
    case 1:
    default:
      ret.push('individual', 'startup', 'enterprise');
      break;
  }

  let _excludeOptions: Array<PlanType> = [];
  if (config?.excludeOption)
    _excludeOptions =
      typeof config?.excludeOption === 'string' ? [config?.excludeOption] : config?.excludeOption;

  return ret.filter((option) => !_excludeOptions.find((o) => o === option));
};

const getPlanVariantDetailsCardProps = (
  planType: PlanType,
  planVariants: Array<PlanVariantResponse>,
  pageContent: StoryContent,
  selectedInterval: SubscriptionInterval,
  router: NextRouter,
  isEnterprisePricingPage: boolean = false
): PlanVariantDetailsCardComponentProps => {
  const getLicenseIdFromQuery = (): LicenseId => {
    const { license_id, plan_code } = router.query;
    if (license_id) return parseInt(router.query.license_id as string) as LicenseId;
    if (plan_code) {
      const decoded = decodeLicenseId(plan_code as string);
      return parseInt(decoded) as LicenseId;
    }
    return null;
  }
  const licenseIdFromQuery: LicenseId = getLicenseIdFromQuery()
    ? getLicenseIdFromQuery()
    : null;
  const shouldDisplayEnterprisePlans = router.asPath.includes('enterprise');
  let licenseId: LicenseId;
  let planCardDetails: PlanCardDetails;
  let filteredPlanVariants: Array<PlanVariantResponse>;

  switch (planType) {
    case 'free':
      planCardDetails = pageContent.plan_card_details.find((d) => d.license_id === '0');
      break;

    case 'individual':
      filteredPlanVariants = planVariants.filter((pv) => pv.plan.name.toLowerCase() === planType);
      planCardDetails = pageContent.plan_card_details.find((d) => d.license_id === '1');
      licenseId = 1;
      break;

    case 'startup':
      filteredPlanVariants = planVariants.filter((pv) => pv.plan.name.toLowerCase() === planType);
      planCardDetails = pageContent.plan_card_details.find((d) => d.license_id === '2');
      licenseId = 2;
      break;

    case 'enterprise':
      filteredPlanVariants = planVariants?.filter((pv) => pv.plan.name.toLowerCase() === planType);
      const enterprisePlanDetails = pageContent.plan_card_details.find((d) => d.license_id === '3');

      if (isEnterprisePricingPage) {
        const startupPlanDetails = pageContent.plan_card_details.find((d) => d.license_id === '2');

        planCardDetails = {
          ...enterprisePlanDetails,
          features: [...startupPlanDetails.features.slice(0, -1), ...enterprisePlanDetails.features]
        };
      } else {
        planCardDetails = enterprisePlanDetails;
      }

      licenseId = 3;
      break;

    default:
      break;
  }

  return {
    selectedInterval,
    planCardDetails,
    planVariants: filteredPlanVariants,
    freePlan: planType === 'free',
    licenseId: licenseIdFromQuery || licenseId,
    displayContactSalesUI: planType === 'enterprise' && !shouldDisplayEnterprisePlans,
    testId: `${planType}-plan-card`,
    planType
  };
};

const pricingHelpers = {
  filterPlanCards,
  fetchPricingServersideProps,
  getPlanVariantDetailsCardProps
};

export default pricingHelpers;
